/**
 * Copyright (c) [2024] Jangouts
 *
 * This software may be modified and distributed under the terms
 * of the MIT license.  See the LICENSE.txt file for details.
 */

import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Phone, PhoneOff, User } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { actionCreators as roomActions } from '../../state/ducks/room';
import { actionCreators as callActions } from "../../state/ducks/call";

// 响铃音频
import ringingAudio from "../../assets/ringing.mp3";

/**
 * 来电界面组件
 * 显示来电通知和接听/拒接界面
 */
const IncomingCall = ({
  callId,
  callerId,
  callerName,
  roomId,
  onAccept,
  onReject,
}) => {
  const dispatch = useDispatch();
  const [callDuration, setCallDuration] = useState(0);
  const [isRinging, setIsRinging] = useState(true);
  const currentCall = useSelector((state) => state.call.currentCall);
  const userInfo = useSelector((state) => state.user.userInfo);
  const { settings } = useSelector((state) => state.room);
  const audioRef = useRef(null);
  const [audioBlocked, setAudioBlocked] = useState(false);

  // 响铃音效（只在挂载时执行一次）
  useEffect(() => {
    setIsRinging(true);
    setAudioBlocked(false);

    const audio = new Audio(ringingAudio);
    audio.loop = true;
    audio.volume = 0.7;
    audioRef.current = audio;

    // 分层播放策略
    (async () => {
      try {
        await audio.play();
        // 成功：直接播放
      } catch {
        // 失败：尝试静音播放
        audio.muted = true;
        try {
          await audio.play();
          // 静音播放成功，等待用户交互后取消静音
          setAudioBlocked(true);
        } catch {
          // 静音也失败（iOS），标记为拦截
          setAudioBlocked(true);
        }
      }
    })();

    // 用户交互后解锁音频
    const handleInteraction = () => {
      if (!audioRef.current) return;
      const a = audioRef.current;
      if (a.muted) {
        a.muted = false;
        a.play().catch(() => {});
      } else if (a.paused) {
        a.play().catch(() => {});
      }
      setAudioBlocked(false);
    };
    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('touchstart', handleInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // 60秒倒计时 + 自动拒接
  useEffect(() => {
    if (callDuration >= 60) {
      handleRejectCall("无人接听");
      return;
    }
    const timer = setTimeout(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [callDuration]);

  const handleAcceptCall = async () => {
    console.log(`Accepting call from ${callerName}`);
    setIsRinging(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    console.log(currentCall);
    console.log(`Accepting call from ${callerName}`);
    // 更新Redux状态为接听
    dispatch(callActions.acceptCall(callId));
    // 加入房间
    //const res = await janusApi.enterRoom({ id: currentCall.roomId }, userInfo.nickname, "");
    const newSettings = { ...settings, username: userInfo.nickname, roomId: currentCall.roomId};
    dispatch(roomActions.saveSettings(newSettings));
    dispatch(roomActions.login(userInfo.nickname, currentCall.roomId));
    if (onAccept) {
      onAccept(callId, currentCall.roomId);
    }
  };

  const handleRejectCall = (reason = "用户拒接") => {
    console.log(`Rejecting call from ${callerName}: ${reason}`);
    setIsRinging(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    // 更新Redux状态为拒接
    dispatch(callActions.rejectCall(callId));
    if (onReject) {
      onReject(reason);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        {/* 来电者信息 */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <User size={48} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {callerName}
          </h2>
         
        </div>

        {/* 响铃状态 */}
        <div className="text-center mb-8">
          <div className="text-3xl font-mono font-bold text-gray-800 mb-2">
            {formatDuration(callDuration)}
          </div>

          <div className="flex justify-center space-x-4 mb-4">
            {isRinging ? (
              <div className="flex items-center text-green-600">
                <div className="w-2 h-2 rounded-full bg-green-600 animate-ping mr-2"></div>
                <span className="font-medium">来电中</span>
                {audioBlocked && (
                  <span className="ml-2 text-xs text-gray-400">（点击按钮开启铃声）</span>
                )}
              </div>
            ) : (
              <div className="flex items-center text-gray-600">
                <span className="font-medium">处理中...</span>
              </div>
            )}
          </div>

          {/* 响铃动画 */}
          {isRinging && (
            <div className="flex justify-center space-x-2">
              <div
                className="w-4 h-4 rounded-full bg-green-400 animate-bounce"
                style={{ animationDelay: "0ms" }}
              ></div>
              <div
                className="w-4 h-4 rounded-full bg-green-400 animate-bounce"
                style={{ animationDelay: "150ms" }}
              ></div>
              <div
                className="w-4 h-4 rounded-full bg-green-400 animate-bounce"
                style={{ animationDelay: "300ms" }}
              ></div>
            </div>
          )}
        </div>

        {/* 操作按钮 */}
        <div className="flex justify-center space-x-6">
          {/* 拒接按钮 */}
          <button
            onClick={() => handleRejectCall("用户拒接")}
            className="flex items-center justify-center px-8 py-4 bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors"
            title="拒接"
          >
            <PhoneOff size={24} className="mr-2" />
            <span className="font-bold">拒接</span>
          </button>

          {/* 接听按钮 */}
          <button
            onClick={handleAcceptCall}
            className="flex items-center justify-center px-8 py-4 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
            title="接听"
          >
            <Phone size={24} className="mr-2" />
            <span className="font-bold">接听</span>
          </button>
        </div>

        {/* 提示信息 */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>来自 {callerName} 的通话请求</p>
          <p className="mt-1">如果长时间无人接听，呼叫将自动取消</p>
        </div>

        

    
      </div>
    </div>
  );
};

IncomingCall.propTypes = {
  callId: PropTypes.string.isRequired,
  callerId: PropTypes.string.isRequired,
  callerName: PropTypes.string.isRequired,
  roomId: PropTypes.string.isRequired,
  onAccept: PropTypes.func,
  onReject: PropTypes.func,
};

export default IncomingCall;
